/**
 * ProjectSidebar — project navigator only (places).
 * Search · New Project · Recent · All · Archived
 * No grey scrim on laptop — Finder-like edge overlay / push.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MagnifyingGlass,
  Plus,
} from '@phosphor-icons/react';
import { fetchDeals, fetchDeal } from './SnapshotManager';
import {
  displayVerdictForDeal,
  reconcileDealListVerdict,
} from '../utils/sidebarVerdict';
import { EvaLogo } from './EvaLogo';

function verdictTone(verdict) {
  if (verdict === 'GO') return 'text-emerald-700 bg-emerald-50';
  if (verdict === 'WATCH') return 'text-amber-700 bg-amber-50';
  if (verdict === 'NO GO') return 'text-red-700 bg-red-50';
  return 'text-gray-500 bg-gray-50';
}

const SCOPES = [
  { id: 'recent', label: 'Recent' },
  { id: 'all', label: 'All' },
  { id: 'archived', label: 'Archived' },
];

export function ProjectSidebar({
  open,
  pinned = false,
  onClose,
  onOpenProject,
  onNewProject,
  currentFileId,
  /** Live combined verdict for the open project (financial + active-scenario land). */
  activeLiveVerdict = null,
  /** Latest successful save/autosave patch for one deal (server verdict). */
  dealVerdictReconcile = null,
}) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('recent');

  const refresh = useCallback(() => {
    setLoading(true);
    fetchDeals()
      .then((d) => {
        let list = Array.isArray(d) ? d : [];
        if (dealVerdictReconcile?.id) {
          list = reconcileDealListVerdict(list, dealVerdictReconcile);
        }
        setDeals(list);
      })
      .finally(() => setLoading(false));
  }, [dealVerdictReconcile]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // Apply save reconciliation immediately to the in-memory list (no refetch wait).
  useEffect(() => {
    if (!dealVerdictReconcile?.id) return;
    setDeals((prev) => reconcileDealListVerdict(prev, dealVerdictReconcile));
  }, [dealVerdictReconcile]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let pool;
    if (scope === 'archived') {
      pool = deals.filter((d) => d.archived);
    } else {
      pool = deals.filter((d) => !d.archived);
    }
    const list = q
      ? pool.filter((d) => (d.name || '').toLowerCase().includes(q))
      : pool;
    const sorted = [...list].sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
    );
    if (scope === 'recent') return sorted.slice(0, 12);
    return sorted;
  }, [deals, query, scope]);

  const handleSelect = async (deal) => {
    const full = await fetchDeal(deal.id);
    if (full) {
      onOpenProject?.(full);
      if (!pinned) onClose?.();
    }
  };

  // Visibility is controlled only by `open`. `pinned` is layout mode (push vs overlay).
  if (!open) return null;

  const panel = (
    <aside
      className={`flex flex-col h-full bg-[#f5f5f7] border-r border-black/10 ${
        pinned ? 'w-[270px] shrink-0' : 'w-[280px] max-w-[85vw]'
      }`}
      data-testid="project-sidebar"
      aria-label="Projects"
    >
      <div className="h-11 flex items-center gap-2 px-3 border-b border-black/5 shrink-0">
        <EvaLogo className="h-4 shrink-0" />
        <span className="text-[13px] font-semibold text-gray-900 tracking-tight">Projects</span>
        <span className="text-[11px] text-gray-400 tabular-nums">
          {deals.filter((d) => !d.archived).length}
        </span>
        <div className="flex-1" />
      </div>

      <div className="px-3 pt-3 pb-2 space-y-2 shrink-0">
        <div className="relative">
          <MagnifyingGlass weight="bold" className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="w-full h-9 pl-8 pr-2 rounded-lg bg-white border border-black/10 text-[13px] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            data-testid="sidebar-search"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            onNewProject?.();
            if (!pinned) onClose?.();
          }}
          className="w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white border border-black/10 text-[13px] font-medium text-gray-900 hover:bg-gray-50"
          data-testid="sidebar-new-project"
        >
          <Plus weight="bold" className="w-3.5 h-3.5" />
          New Project
        </button>
        <div className="flex p-0.5 rounded-lg bg-black/5" role="tablist" aria-label="Project scope">
          {SCOPES.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={scope === s.id}
              onClick={() => setScope(s.id)}
              className={`flex-1 h-7 rounded-md text-[11px] font-medium ${
                scope === s.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
              data-testid={`sidebar-scope-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {loading ? (
          <p className="text-[12px] text-gray-400 px-2 py-3">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-[12px] text-gray-400 px-2 py-3">No projects</p>
        ) : (
          <ul className="space-y-0.5">
            {filtered.map((deal) => {
              const active = deal.id === currentFileId;
              const { verdict, reason, live } = displayVerdictForDeal(deal, {
                currentFileId,
                activeLiveVerdict,
              });
              return (
                <li key={deal.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(deal)}
                    className={`w-full text-left px-2.5 py-2 rounded-lg min-h-[44px] transition-colors ${
                      active ? 'bg-blue-600 text-white' : 'hover:bg-black/5 text-gray-900'
                    }`}
                    data-testid={`sidebar-project-${deal.id}`}
                  >
                    <div className="text-[13px] font-medium truncate leading-tight">{deal.name || 'Untitled'}</div>
                    <div className={`mt-0.5 flex items-center gap-1.5 text-[11px] ${active ? 'text-white/80' : 'text-gray-500'}`}>
                      {verdict ? (
                        <span
                          className={`px-1.5 py-px rounded ${active ? 'bg-white/20 text-white' : verdictTone(verdict)}`}
                          title={reason || undefined}
                          data-testid={`sidebar-verdict-${deal.id}`}
                          data-live-verdict={live ? 'true' : 'false'}
                        >
                          {verdict}
                        </span>
                      ) : null}
                      <span className="truncate">
                        {deal.updatedAt ? new Date(deal.updatedAt).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );

  if (pinned) {
    // In-flow so collapsing the sidebar lets the main pane reclaim width.
    return (
      <div
        className="hidden xl:flex h-full shrink-0"
        data-testid="project-sidebar-pinned"
      >
        {panel}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80]" data-testid="project-sidebar-overlay">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-transparent"
        aria-label="Dismiss sidebar"
        onClick={onClose}
      />
      <div className="absolute inset-y-0 left-0 shadow-xl shadow-black/10 pointer-events-auto">
        {panel}
      </div>
    </div>
  );
}
